import React, { useEffect, useRef, useState } from "react";
import { Volume2, Square, Loader2 } from "lucide-react";
import { trackEvent } from "../lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PREFERRED_VOICES = [/Samantha/i, /Google US English/i, /Microsoft Aria/i, /Microsoft Jenny/i, /Google UK English/i, /en-US/i, /en-GB/i, /en[-_]/i];

const pickVoice = () => {
  const voices = window.speechSynthesis?.getVoices() || [];
  for (const rx of PREFERRED_VOICES) {
    const v = voices.find((x) => rx.test(x.name || "") || rx.test(x.lang || ""));
    if (v) return v;
  }
  return voices[0] || null;
};

// Plays a cached ElevenLabs track; falls back to the browser voice if unavailable.
export const NarrationButton = ({ track, fallbackText, testId, label = "Listen", className = "", onEnded, autoPlay = false }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const failedRef = useRef(false);
  const countedRef = useRef(false);

  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch (e) { void e; }
    try { window.speechSynthesis?.cancel(); } catch (e) { void e; }
  }, []);

  const speakFallback = () => {
    if (!window.speechSynthesis || !fallbackText) { setPlaying(false); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(fallbackText);
    const v = pickVoice();
    if (v) u.voice = v;
    u.rate = 0.95;
    u.onend = () => { setPlaying(false); onEnded?.(); };
    u.onerror = () => setPlaying(false);
    window.speechSynthesis.speak(u);
    setPlaying(true);
  };

  const play = () => {
    window.dispatchEvent(new Event("aip-media-play"));
    if (!countedRef.current) { countedRef.current = true; trackEvent("listen", track); }
    if (failedRef.current) { speakFallback(); return; }
    if (!audioRef.current) {
      const a = new Audio(`${BACKEND_URL}/api/narration/${track}`);
      a.preload = "auto";
      a.onended = () => { setPlaying(false); onEnded?.(); };
      a.onplaying = () => { setLoading(false); setPlaying(true); };
      a.onerror = () => { failedRef.current = true; setLoading(false); speakFallback(); };
      audioRef.current = a;
    }
    setLoading(true);
    audioRef.current.play().catch(() => {});
  };

  useEffect(() => {
    if (autoPlay) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = () => {
    try { audioRef.current?.pause(); } catch (e) { void e; }
    try { window.speechSynthesis?.cancel(); } catch (e) { void e; }
    setLoading(false);
    setPlaying(false);
  };

  const active = playing || loading;
  return (
    <button
      type="button"
      onClick={active ? stop : play}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs md:text-sm font-orbitron uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${active ? "bg-red-500/15 border-red-400/40 text-red-200 hover:bg-red-500/25" : "bg-cyan-500/10 border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/20"} ${className}`}
      data-testid={testId}
      aria-label={active ? "Pause narration" : label}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : playing ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      <span>{loading ? "Loading" : playing ? "Pause" : label}</span>
    </button>
  );
};
